"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, Users, CircleCheck as CheckCircle2, ArrowRight, Clock, HardHat, FileCheck2, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accreditations = [{
  name: "NASC",
  title: "National Access & Scaffolding Confederation",
  description: "Fully audited adherence to UK scaffolding safety criteria, technical guidance, and site compliance.",
  badge: "Audited & Verified"
}, {
  name: "CHAS",
  title: "Contractors Health and Safety Assessment",
  description: "Rigorous health and safety compliance demonstrating robust risk assessments and site management.",
  badge: "Health & Safety"
}, {
  name: "Constructionline",
  title: "Pre-Qualified Contractor Standard",
  description: "Verified operational standards, financial stability, and governance for commercial projects.",
  badge: "Pre-Qualified"
}];
const pillars = [{
  icon: Clock,
  title: "20+ years site leadership",
  summary: "Led by Pete Wood",
  description: "Pete Wood brings over two decades of hands-on trade experience, supervising complex residential, solar, and commercial scaffolding across the Home Counties."
}, {
  icon: Users,
  title: "Family-run accountability",
  summary: "Personal care & legacy",
  description: "We carry forward Adam Story's legacy with genuine care and personal ownership on every job. You deal directly with committed decision-makers."
}, {
  icon: ShieldCheck,
  title: "Safety without compromise",
  summary: "Strict site standards",
  description: "Every structure is planned, erected, inspected, and handed over following strict TG20 and SG4 working guidelines to protect trades and property."
}];
const checklistItems = ["Direct oversight by Pete Wood on major project setups", "NASC and CHAS safety-audited procedures", "Transparent project pricing with zero hidden surcharges", "Active across Berkshire, Surrey, Hampshire, Buckinghamshire & London"];
export default function Experience() {
  return <section id="aboutexperienceaccreditations" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/20 text-primary">
            Experience &amp; Standards
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
            Built on 20+ years of hands-on site leadership
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd combines family-run accountability with professional contractor discipline. Led by Pete Wood and carrying forward Adam Story&apos;s legacy, we deliver dependable access across the South East.
          </p>
        </div>

        {/* 3 Pillar Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200">
                  <CardHeader className="pb-3">
                    <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                      <IconComponent className="size-6 text-primary" />
                    </div>
                    <Badge variant="secondary" className="w-fit text-xs mb-2">
                      {pillar.summary}
                    </Badge>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Accreditations & Experience Deep Dive */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-16 bg-muted/40 rounded-2xl border border-border p-6 sm:p-8 lg:p-12">
          {/* Left Column: Pete Wood & Team Ethos */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <HardHat className="size-4" />
              <span>Certified Operational Standards</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Safety-led scaffolding with complete accountability
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              We do not compete to be the cheapest; we compete on safety, precision, and reliable handovers. Roofing contractors, solar installers, developers, and local authorities trust BMF Scaffolding because we show up on schedule, erect safely, and document every step.
            </p>
            
            <ul className="space-y-3 pt-2">
              {checklistItems.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground font-medium">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>)}
            </ul>

            <div className="pt-4 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="rounded-md font-medium">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>View all accreditations</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Confirmed Accreditations Grid */}
          <div className="lg:col-span-6 space-y-4">
            <div className="p-4 bg-card rounded-xl border border-border shadow-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Industry Verification
                </span>
                <Badge variant="default" className="text-xs bg-primary text-primary-foreground">
                  100% Compliant
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                All credentials actively registered and maintained across all active contracts.
              </p>
            </div>

            <div className="space-y-3">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="bg-card border border-border rounded-xl p-5 shadow-xs hover:border-primary/40 transition-[border-color] duration-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-sm shrink-0">
                        {acc.name}
                      </div>
                      <div>
                        <h4 className="font-bold text-base text-card-foreground">
                          {acc.name} Accredited
                        </h4>
                        <p className="text-xs text-muted-foreground font-medium">
                          {acc.title}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-2xs font-semibold">
                      {acc.badge}
                    </Badge>
                  </div>
                  <Separator className="my-3" />
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {acc.description}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Trust & Coverage Footer strip */}
        <div className="text-center pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Building2 className="size-4 text-primary" />
            <span>Serving Berkshire, Surrey, Hampshire, Buckinghamshire, London &amp; Home Counties</span>
          </div>
          <div className="flex items-center gap-2">
            <FileCheck2 className="size-4 text-primary" />
            <span>Fully insured with comprehensive public &amp; employers liability</span>
          </div>
        </div>
      </div>
    </section>;
}